import { ArrowTriangleDownIcon } from '@tableau/icons/ArrowTriangleDownIcon';
import { Tooltip, TooltipPosition } from '@tableau/widgets';
import * as React from 'react';
import { classes, forceRenderStyles } from 'typestyle';
import { NestedCSSProperties } from 'typestyle/lib/types';
import { ItemType, ToolbarColorTheme, useToolbarColorThemeContext } from '../PublicTypes';
import {
  DefaultButtonStyleName,
  DropdownArrowContainerStyleName,
  HiddenClassName,
  HiddenLabelStyleName,
  HighlightedButtonStyleName,
  LabelStyleName,
  NoIconDropdownOverrideStyles,
  SplitButtonOverrideStyles,
} from '../styles/ButtonStyles';
import { IconFromId, ToolbarIconId } from './Icons';

export const ToolbarButtonLabelClass = 'toolbar-button-label';

type ButtonType = ItemType | 'dropdown';
export const IconIdAttr = 'data-toolbar-icon-id';
export interface ButtonProps extends Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled' | 'onClick' | 'tabIndex'> {
  /** A ref to the button element. */
  buttonRef?: React.Ref<HTMLButtonElement>;
  /** Button type affects how the button is styled. If not provided, default styling is applied. */
  buttonType?: ButtonType;
  /** If provided, description will be shown in a Tooltip (widget) on the button. */
  description?: string;
  /** Unique identifier for icon. */
  iconId: string;
  /** Unique identifier for the button */
  id: string;
  /** Label associated with the button */
  label: string;
  /** Set to true to show the button in selected state. */
  selected?: boolean;
  /** Set to true to show `label` in the button, otherwise shows icon only */
  showLabel?: boolean;
  /** Unique identifier */
  testId: string;
  /** If `true`, do not show an icon */
  omitIcon?: boolean;
  /** If `true`, button is hidden from view with `display: none` */
  isHidden?: boolean;
}

export const ButtonTestIdPrefix = 'viz-viewer-toolbar-button-';
export const ButtonLabelTestId = 'viz-viewer-toolbar-button-label';

export function Button(props: ButtonProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonProps>): React.ReactElement {
  const colorTheme: ToolbarColorTheme = useToolbarColorThemeContext();

  const { buttonRef, buttonType, className, description, isHidden, iconId, label, omitIcon, selected, showLabel, testId, ...forwardProps } =
    props;
  const isDropdown = buttonType === 'dropdown';

  let iconSize = 18;
  let styleOverrides: NestedCSSProperties = {};
  const hideIcon = omitIcon || !iconId; // check for empty string iconId
  let typeClass = 'button';
  if (isDropdown) {
    typeClass = 'dropdown-button';
    if (hideIcon) styleOverrides = NoIconDropdownOverrideStyles;
  } else if (buttonType === 'splitButton') {
    styleOverrides = SplitButtonOverrideStyles;
  } else if (buttonType === 'highlightedButton') {
    iconSize = 14;
  } else if (buttonType === 'popoverButton') {
    typeClass = 'popover-button';
  }

  const buttonClassName =
    buttonType === 'highlightedButton'
      ? HighlightedButtonStyleName(colorTheme)
      : classes(typeClass, DefaultButtonStyleName(colorTheme, selected, styleOverrides));
  const labelClassName = classes(ToolbarButtonLabelClass, showLabel ? LabelStyleName : HiddenLabelStyleName);
  forceRenderStyles(); // otherwise the button styles aren't fully applied when the menu is positioned. FIXME can we make those stylenames static?

  return (
    <Tooltip afterDelay={true} tooltipContent={props.disabled ? undefined : description} position={TooltipPosition.Bottom}>
      <button
        {...forwardProps}
        id={props.id}
        // Include the className prop since the PopoverWidget will
        // add its own class to the trigger element.
        className={classes(buttonClassName, className, { [HiddenClassName]: isHidden })}
        data-eventutils-optout={true}
        data-tb-test-id={`${ButtonTestIdPrefix}${testId}`}
        {...{ [IconIdAttr]: iconId }}
        ref={buttonRef}
        aria-pressed={selected}
      >
        {!hideIcon && (IconFromId[iconId as ToolbarIconId] || IconFromId.placeholder)({ size: iconSize })}
        <span className={labelClassName} data-tb-test-id={ButtonLabelTestId}>
          {label}
        </span>
        {isDropdown && (
          <span className={classes('dropdown-arrow', DropdownArrowContainerStyleName)} style={{ marginLeft: !hideIcon ? 1 : 0 }}>
            <ArrowTriangleDownIcon size={12} />
          </span>
        )}
      </button>
    </Tooltip>
  );
}
